/* eslint-disable react/no-unused-state */
import _ from 'lodash';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';

import cn from 'classnames';
import ContentEditable from 'react-contenteditable';

import { isLayerType } from 'common/utils/is-layer-type';
import { triggerFocus } from 'common/utils/helpers';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import { BBCommonProps, StoryModel } from 'types/story';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { CHILDREN_KEY } from 'client/components/common/BuildingBlocks/utils/common';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import css from 'src/routes/client/components/common/BuildingBlocks/Share/Share.scss';
import { ClientReducerState } from 'client/reducers';
import { ApiEvents } from 'client/ApiEvents';
import { StoryFacade } from 'common/utils/facades/story-facade';
import Img from '../Img/Img';
import { SHARE_TYPE } from './constants';
import { Icon } from './Icon';

const mapStateToProps = (state: ClientReducerState) => ({
	story: state.story,
	memberId: state.user.id,
	cardId: state.card._id!,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

type State = {
	htmlPrev: string;
	html: string;
	editing: boolean;
};

type Props = RouteComponentProps & ReduxProps & BBCommonProps & { className?: string; children: string };

class Share extends React.Component<Props, State> {
	editableRef = React.createRef<HTMLElement>();

	state = {
		htmlPrev: '',
		html: '',
		editing: false,
	};

	static getDerivedStateFromProps({ children }: Props, state: State) {
		const isUpdatedChildrenFromProp = children !== state.htmlPrev;
		if (isUpdatedChildrenFromProp) {
			return { html: children, htmlPrev: children };
		}
		return null;
	}

	get story() {
		const story = this.props.story ?? {};
		return new StoryFacade(story as StoryModel, StoryFacade.VERSIONS.latest);
	}

	onEditablePaste = event => {
		// cancel paste
		event.preventDefault();
		// get text representation of clipboard
		const text = (event.originalEvent || event).clipboardData.getData('text/plain');
		// insert text manually
		const command = document.queryCommandSupported('insertText') ? 'insertText' : 'paste';
		document.execCommand(command, false, text);
	};

	onEditableBlur = event => {
		const path = _.get(this.props.editableModeProps, ['nodeProps', 'data-path']);
		const { currentMediaQuery } = this.props;

		// NOTE: children is always writes to defaultState, but for certain media query
		if (isLayerType(this.props).global) {
			transmitToAdminBbUpdate({
				id: Share.name,
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: this.state.html,
				isStory: true,
			});
		} else {
			transmitToAdminBbUpdate({
				id: Share.name,
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: this.state.html,
				isStory: false,
			});
		}

		this.setState({ editing: false });
	};

	onEditableChange = event => {
		this.setState({ html: event.target.value });
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);
	};

	onEditableDoubleClick = () => {
		this.setState({ editing: true }, () => {
			this.editableRef.current?.focus();
		});
	};

	get nonEditorListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			onClick: async event => {
				_.invoke(this.props.eventListeners, 'onClick', event);

				const type = this.props.uiConfig.componentProps[UNIFORM_PROPS.shareType];
				const isShareCard = this.props.uiConfig.componentProps[UNIFORM_PROPS.shareCard];
				const shareMsg = this.story.settings.cards?.[this.props.cardId]?.share?.msg;
				if (type) {
					share(type, Boolean(isShareCard), this.story.getTransformedDynamicText(shareMsg));
					let sharePlatform: Parameters<typeof ApiEvents.sendShareEvent>[0]['sharePlatform'] | null = null;

					switch (type) {
						case 'Facebook':
							sharePlatform = 'facebook';
							break;

						case 'Twitter':
							sharePlatform = 'twitter';
							break;

						case 'WhatsApp':
							sharePlatform = 'whatsapp';
							break;

						case 'E-mail':
							sharePlatform = 'email';
							break;

						default:
							break;
					}

					if (sharePlatform) {
						ApiEvents.sendShareEvent({
							organizationId: this.story.base.organizationId,
							storyId: this.story.storyId,
							cardId: this.props.cardId,
							teamId: this.story.base.teamId,
							memberId: this.story.base.createdBy,
							sharePlatform,
						});
					}
				}
			},
		};
	}

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const nodeProps = _.merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);

		/**
		 * Why do we assign "updater" to className?
		 * This is a temporary fix, to update <ContentEditable> in case of
		 * some state was provided via nodeProps. Otherwise <ContentEditable> won't be
		 * updated, because of his own shouldComponentUpdate logic, will return false
		 * if there were changed some data attributes only.
		 */
		let updater = '';
		const updaterObj = { ...nodeProps, ...this.props.stateAttrs };
		Object.keys(updaterObj).forEach(k => {
			const res = k.match(/^(data-)/);
			if (res) updater += `${k}=${updaterObj[k]}`;
		});

		return this.renderDefault(
			{
				...nodeProps,
				onDoubleClick: this.onEditableDoubleClick,
			},
			<ContentEditable
				innerRef={this.editableRef}
				className={cn(css.btnContent, updater)}
				tagName="div"
				html={this.state.html}
				// disabled={!this.state.editing}
				disabled
				onChange={this.onEditableChange}
				onBlur={this.onEditableBlur}
				onPaste={this.onEditablePaste}
			/>
		);
	}

	renderDefault(props: any = {}, editableContent?: React.ReactNode) {
		const { nodeProps } = this.props.uiConfig;
		const bgImage = this.props.uiConfig.componentProps.styles.backgroundImage;
		const hasBgImage = bgImage && bgImage !== 'none';

		return (
			<button
				type="button"
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.nonEditorListeners}
				{...props}
				className={cn(css.btn, nodeProps.className, props.className, { [css.editable]: !!editableContent })}
				ref={this.props.containerRef}
			>
				{hasBgImage ? (
					<Img {...this.props} custom={{ style: { width: '100%', height: '100%' } }} />
				) : (
					<div className={css.btnIcon}>
						<Icon type={this.props.uiConfig.componentProps.shareType} />
					</div>
				)}
				{editableContent || (
					<div
						className={css.btnContent}
						/* eslint-disable-next-line react/no-danger */
						dangerouslySetInnerHTML={{ __html: this.state.html }}
					/>
				)}
			</button>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export default withCardTransitionContext(withRouter(connector(Share)));

function share(type: typeof SHARE_TYPE[keyof typeof SHARE_TYPE], isShareCard: boolean, msg?: string) {
	const { location: l } = window;
	const url = encodeURIComponent(isShareCard ? l.href : `${l.origin}${l.pathname}`);
	const title = encodeURIComponent(document.title);
	const lineBreak = encodeURIComponent('\n');
	const shareMsg = msg ? encodeURIComponent(msg) : title;

	switch (type) {
		case SHARE_TYPE.email:
			window.open(
				`mailto:?subject=${encodeURIComponent('I wanted you to see this site')}&body=${encodeURIComponent(
					'Check out this site'
				)}${msg ? lineBreak + shareMsg : ''}${lineBreak}${url}`
			);
			break;
		case SHARE_TYPE.fb:
			window.open(`https://www.facebook.com/sharer.php?u=${l.href}`, 'sharer', 'width=555,height=600');
			break;
		case SHARE_TYPE.wa:
			window.open(
				`${
					window.screen.width > 1024 ? 'https://web.whatsapp.com/' : 'whatsapp://'
				}send?text=${shareMsg}${lineBreak}${url}`
			);
			break;
		case SHARE_TYPE.tw:
			window.open(`https://twitter.com/share?text=${shareMsg}&url=${lineBreak}${url}&tags=storycards`);
			break;
		default:
			break;
	}
}
